import React, { useState, useEffect, useMemo } from 'react';
import { Tabs } from '../ui/SharedUI';
import SmartDateInput from '../ui/SmartDateInput';
import { demoRepository } from '../../repository/demoRepository';
import { rentScheduleService } from '../../services/rentScheduleService';
import SafeIcon from '../../common/SafeIcon';

const CalculationHelp = ({ children, summary = 'How this is calculated' }) => (
  <details className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
    <summary className="cursor-pointer select-none font-medium text-slate-700 flex items-center gap-1.5">
      <SafeIcon icon="Info" className="text-slate-400" />
      {summary}
    </summary>
    <div className="mt-2 leading-5 text-slate-600">{children}</div>
  </details>
);

const RentScheduleGenerateForm = ({ onSubmit, onCancel, isSubmitting = false, initialPropertyId = '', lockProperty = false, initialAgentFeePercent = null }) => {
  const [activeTab, setActiveTab] = useState('selection');
  const [selectedProperty, setSelectedProperty] = useState(initialPropertyId || '');
  const [selectedSpace, setSelectedSpace] = useState('');
  const [selectedLease, setSelectedLease] = useState('');
  const [scheduleSource, setScheduleSource] = useState('lease');
  const [manualStartDate, setManualStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [manualEndDate, setManualEndDate] = useState('');
  const [manualWeeklyRent, setManualWeeklyRent] = useState('');
  const [manualRentBasis, setManualRentBasis] = useState('weekly');
  const [manualPaymentFrequency, setManualPaymentFrequency] = useState('weekly');
  const [manualRentInAdvance, setManualRentInAdvance] = useState(0);

  const [agentFeePercent, setAgentFeePercent] = useState(initialAgentFeePercent !== null ? initialAgentFeePercent : 5);
  const [otherDeductions, setOtherDeductions] = useState(0);
  const [previewData, setPreviewData] = useState(null);
  const [calculationError, setCalculationError] = useState('');

  const properties = useMemo(() => demoRepository.getAllProperties().filter(p => p.status === 'Active'), []);
  const spaces = useMemo(() => selectedProperty ? demoRepository.getSpacesByProperty(selectedProperty) : [], [selectedProperty]);
  const leases = useMemo(() => {
    let allLeases = demoRepository.getAllLeases().filter(l => demoRepository.isContractualLease(l));
    if (selectedProperty) {
      allLeases = allLeases.filter(l => l.propertyId === selectedProperty);
    }
    if (selectedSpace) {
      allLeases = allLeases.filter(l => l.spaceId === selectedSpace);
    }
    return allLeases;
  }, [selectedProperty, selectedSpace]);

  // Handle locked property auto-select active lease
  useEffect(() => {
    if (lockProperty && initialPropertyId && leases.length === 1 && !selectedLease) {
      setSelectedLease(leases[0].id);
    }
  }, [lockProperty, initialPropertyId, leases, selectedLease]);

  const handleScheduleSourceChange = (source) => {
    setScheduleSource(source);
    setSelectedLease('');
    setPreviewData(null);
    setCalculationError('');
    setActiveTab('selection');
  };

  const buildPropertySpaceScheduleSource = () => {
    const selectedPropertyRecord = properties.find(p => p.id === selectedProperty);
    const selectedSpaceRecord = selectedSpace ? spaces.find(s => s.id === selectedSpace) : null;

    return {
      id: '',
      leaseId: '',
      propertyId: selectedProperty,
      spaceId: selectedSpace || '',
      tenantId: '',
      tenantDisplayName: 'Tenant not assigned',
      tenantDisplayMode: 'not_assigned',
      sourceType: 'property_space_only',
      startDate: manualStartDate,
      endDate: manualEndDate,
      weeklyRent: parseFloat(manualWeeklyRent) || 0,
      rentBasis: manualRentBasis || 'weekly',
      paymentFrequency: manualPaymentFrequency || 'weekly',
      rentInAdvance: parseFloat(manualRentInAdvance) || 0,
      status: 'Projected',
      propertyName: selectedPropertyRecord?.name || '',
      spaceName: selectedSpaceRecord?.name || 'Whole Property',
      metadata: {
        scheduleSource: 'property_space_only',
        tenantDisplayName: 'Tenant not assigned',
        propertyName: selectedPropertyRecord?.name || '',
        spaceName: selectedSpaceRecord?.name || 'Whole Property'
      }
    };
  };

  useEffect(() => {
    if (scheduleSource === 'lease') {
      if (selectedLease) {
        const lease = leases.find(l => l.id === selectedLease);
        if (lease) {
          const rentCalculation = rentScheduleService.getRentPeriodCalculation(
            lease.weeklyRent ?? lease.rentAmount,
            lease.rentBasis || 'weekly',
            lease.paymentFrequency || 'weekly'
          );
          if (!rentCalculation.supported) {
            setPreviewData(null);
            setCalculationError('This lease uses a custom or unsupported rent basis/payment frequency. MPP will not guess a rent schedule. Update the lease to a supported basis/frequency or define the custom rule before generating.');
            return;
          }
          const preview = rentScheduleService.generateRentSchedule(lease, agentFeePercent, otherDeductions);
          if (preview.length === 0) {
            setPreviewData(null);
            setCalculationError('No rent periods could be generated. Check the lease start date, end date, rent amount, rent basis and payment frequency.');
            return;
          }
          setCalculationError('');
          setPreviewData({
            lease,
            scheduleSource: 'lease',
            tenantDisplayName: '',
            scheduleRows: preview,
            totalGross: preview.reduce((sum, r) => sum + r.grossRentDue, 0),
            totalDeductions: preview.reduce((sum, r) => sum + r.totalDeductions, 0),
            totalNet: preview.reduce((sum, r) => sum + r.netOwnerAmount, 0),
            periodCount: preview.length
          });
          return;
        }
      }

      setPreviewData(null);
      setCalculationError('');
      return;
    }

    if (scheduleSource === 'property_space_only') {
      const hasRequiredManualDetails = selectedProperty && manualStartDate && manualEndDate && (parseFloat(manualWeeklyRent) || 0) > 0;

      if (!hasRequiredManualDetails) {
        setPreviewData(null);
        setCalculationError('');
        return;
      }

      const manualLease = buildPropertySpaceScheduleSource();
      const rentCalculation = rentScheduleService.getRentPeriodCalculation(
        manualLease.weeklyRent,
        manualLease.rentBasis,
        manualLease.paymentFrequency
      );
      if (!rentCalculation.supported) {
        setPreviewData(null);
        setCalculationError('This rent basis/payment frequency cannot be calculated automatically. MPP will not default a custom schedule to weekly.');
        return;
      }
      const preview = rentScheduleService.generateRentSchedule(manualLease, agentFeePercent, otherDeductions);
      if (preview.length === 0) {
        setPreviewData(null);
        setCalculationError('No rent periods could be generated. Check that the end date is on or after the start date and that the rent amount is greater than zero.');
        return;
      }
      setCalculationError('');

      setPreviewData({
        lease: manualLease,
        scheduleSource: 'property_space_only',
        tenantDisplayName: 'Tenant not assigned',
        scheduleRows: preview,
        totalGross: preview.reduce((sum, r) => sum + r.grossRentDue, 0),
        totalDeductions: preview.reduce((sum, r) => sum + r.totalDeductions, 0),
        totalNet: preview.reduce((sum, r) => sum + r.netOwnerAmount, 0),
        periodCount: preview.length
      });
      return;
    }

    setPreviewData(null);
    setCalculationError('');
  }, [
    scheduleSource,
    selectedLease,
    selectedProperty,
    selectedSpace,
    manualStartDate,
    manualEndDate,
    manualWeeklyRent,
    manualRentBasis,
    manualPaymentFrequency,
    manualRentInAdvance,
    agentFeePercent,
    otherDeductions,
    leases,
    properties,
    spaces
  ]);

  const handlePropertyChange = (e) => {
    if (lockProperty) return;
    setSelectedProperty(e.target.value);
    setSelectedSpace('');
    setSelectedLease('');
    setPreviewData(null);
    setCalculationError('');
  };

  const handleSpaceChange = (e) => {
    setSelectedSpace(e.target.value);
    setSelectedLease('');
    setPreviewData(null);
    setCalculationError('');
  };

  const handleLeaseChange = (e) => {
    setSelectedLease(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!previewData) return;

    const submitLease = previewData.lease || {};
    const submitLeaseId = scheduleSource === 'lease' ? selectedLease : '';

    onSubmit({
      leaseId: submitLeaseId,
      lease: submitLease,
      scheduleSource,
      tenantDisplayName: previewData.tenantDisplayName || submitLease.tenantDisplayName || '',
      tenantDisplayMode: submitLease.tenantDisplayMode || '',
      propertyId: submitLease.propertyId || selectedProperty || '',
      spaceId: submitLease.spaceId || selectedSpace || '',
      tenantId: submitLease.tenantId || '',
      scheduleRows: previewData.scheduleRows,
      agentFeePercent,
      otherDeductions,
      summary: {
        totalGross: previewData.totalGross,
        totalDeductions: previewData.totalDeductions,
        totalNet: previewData.totalNet,
        periodCount: previewData.periodCount
      }
    });
  };

  const inputClass = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white";
  const labelClass = "block text-xs font-medium text-slate-600 mb-1";

  const tabList = [
    { id: 'selection', label: 'Schedule Source' },
    { id: 'settings', label: 'Fee Settings' },
    { id: 'preview', label: 'Preview' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Tabs tabs={tabList} activeTab={activeTab} onChange={setActiveTab} />

      {lockProperty && (
        <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
          <div className="text-xs font-medium text-amber-800">
            Generating schedule for <strong>{properties.find(p => p.id === selectedProperty)?.name || 'this property'}</strong>.
          </div>
        </div>
      )}

      {activeTab === 'selection' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleScheduleSourceChange('lease')}
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${scheduleSource === 'lease' ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              <div className="text-sm font-semibold">Generate from Active Lease</div>
              <div className="text-xs text-slate-500 mt-1">Use an existing tenant and lease record.</div>
            </button>

            <button
              type="button"
              onClick={() => handleScheduleSourceChange('property_space_only')}
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${scheduleSource === 'property_space_only' ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              <div className="text-sm font-semibold">Generate without Tenant / Lease</div>
              <div className="text-xs text-slate-500 mt-1">Use property, space, rent and dates only.</div>
            </button>
          </div>

          {!lockProperty && (
            <div>
              <label className={labelClass}>Property *</label>
              <select value={selectedProperty} onChange={handlePropertyChange} className={inputClass}>
                <option value="">Select Property...</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {selectedProperty && !lockProperty && (
            <div>
              <label className={labelClass}>Room/Space (Optional)</label>
              <select value={selectedSpace} onChange={handleSpaceChange} className={inputClass}>
                <option value="">All Spaces</option>
                {spaces.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {scheduleSource === 'lease' && (selectedProperty || selectedSpace) && (
            <div>
              <label className={labelClass}>Active Lease *</label>
              {leases.length > 0 ? (
                <select value={selectedLease} onChange={handleLeaseChange} className={inputClass}>
                  <option value="">Select Lease...</option>
                  {leases.map(l => {
                    const tenant = demoRepository.getTenantById(l.tenantId);
                    const space = l.spaceId ? demoRepository.getSpaceById(l.spaceId) : null;
                    return (
                      <option key={l.id} value={l.id}>
                        {tenant?.firstName} {tenant?.lastName} - {space?.name || 'Whole Property'}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-sm text-slate-500 flex items-center gap-2">
                  <SafeIcon icon="Info" className="text-slate-400" />
                  No active leases found for this property. Add a tenant and active lease before generating a rent schedule.
                </div>
              )}
            </div>
          )}

          {scheduleSource === 'property_space_only' && selectedProperty && (
            <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div>
                <h4 className="text-sm font-semibold text-slate-800">Tenantless Schedule Details</h4>
                <p className="text-xs text-slate-500 mt-1">
                  This schedule will be linked to the property and optional room/space only. Tenant and lease are not required.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Start Date *</label>
                  <SmartDateInput
                    value={manualStartDate}
                    onChange={(e) => setManualStartDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>End Date *</label>
                  <SmartDateInput
                    value={manualEndDate}
                    onChange={(e) => setManualEndDate(e.target.value)}
                    className={inputClass}
                    baseDate={manualStartDate}
                    baseLabel="the schedule start date"
                  />
                </div>

                <div>
                  <label className={labelClass}>Rent Amount *</label>
                  <input
                    type="number"
                    value={manualWeeklyRent}
                    onChange={(e) => setManualWeeklyRent(e.target.value)}
                    className={inputClass}
                    step="0.01"
                    min="0"
                    placeholder="450.00"
                  />
                </div>

                <div>
                  <label className={labelClass}>Rent Basis</label>
                  <select value={manualRentBasis} onChange={(e) => setManualRentBasis(e.target.value)} className={inputClass}>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Payment Frequency</label>
                  <select value={manualPaymentFrequency} onChange={(e) => setManualPaymentFrequency(e.target.value)} className={inputClass}>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Custom frequencies are not guessed; define a supported frequency before generating.</p>
                </div>

                <div>
                  <label className={labelClass}>Rent in Advance</label>
                  <input
                    type="number"
                    value={manualRentInAdvance}
                    onChange={(e) => setManualRentInAdvance(parseFloat(e.target.value) || 0)}
                    className={inputClass}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <CalculationHelp summary="How rent amount and payment frequency work">
                <p>The rent amount is interpreted using <strong>Rent Basis</strong>, then annualised before it is converted to the selected payment frequency.</p>
                <p className="mt-1">Example: $2,600 monthly × 12 = $31,200 annual. If paid fortnightly, $31,200 ÷ 26 = $1,200 gross per payment period.</p>
              </CalculationHelp>
            </div>
          )}

          {calculationError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {calculationError}
            </div>
          )}

          {previewData && (
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-slate-800">Lease Summary</h4>
                <button type="button" onClick={() => setActiveTab('settings')} className="text-amber-600 hover:underline text-xs font-medium">Continue to Settings →</button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Tenant:</span>
                  <div className="font-medium">
                    {previewData.scheduleSource === 'property_space_only'
                      ? 'Tenant not assigned'
                      : previewData.lease && demoRepository.getTenantById(previewData.lease.tenantId)?.firstName}
                  </div>
                </div>
                <div>
                  <span className="text-slate-500">Source:</span>
                  <div className="font-medium">{previewData.scheduleSource === 'property_space_only' ? 'Property / Space Only' : 'Active Lease'}</div>
                </div>
                <div>
                  <span className="text-slate-500">Periods:</span>
                  <div className="font-medium">{previewData.periodCount} rows</div>
                </div>
                <div>
                  <span className="text-slate-500">Total Gross:</span>
                  <div className="font-medium">${previewData.totalGross.toFixed(2)}</div>
                </div>
                <div>
                  <span className="text-slate-500">Total Net:</span>
                  <div className="font-medium">${previewData.totalNet.toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-4">
          {!previewData && (
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-sm text-slate-500">
              Complete the schedule source details first to configure fee settings.
            </div>
          )}

          {previewData && (
            <>
              <div>
                <label className={labelClass}>Agent Management Fee (%)</label>
                <input
                  type="number"
                  value={agentFeePercent}
                  onChange={(e) => setAgentFeePercent(parseFloat(e.target.value) || 0)}
                  className={inputClass}
                  step="0.01"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Typical range: 4-8% for residential, 5-10% for commercial
                </p>
              </div>

              <div>
                <label className={labelClass}>Other Deductions ($ per period)</label>
                <input
                  type="number"
                  value={otherDeductions}
                  onChange={(e) => setOtherDeductions(parseFloat(e.target.value) || 0)}
                  className={inputClass}
                  step="0.01"
                  min="0"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Land rates, water, strata fees, etc. (if applicable per period)
                </p>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-semibold text-amber-800">Fee Calculation Example</h4>
                  <button type="button" onClick={() => setActiveTab('preview')} className="text-amber-700 hover:underline text-xs font-medium">Continue to Preview →</button>
                </div>
                <CalculationHelp summary="Show the actual formula used for this schedule">
                  {(() => {
                    const calc = previewData.scheduleRows[0]?.metadata?.rentCalculation;
                    if (!calc) return <p>Gross rent − agent fee − other deductions = net owner amount.</p>;
                    return (
                      <div className="space-y-1">
                        <p><strong>Annual rent:</strong> ${calc.rentAmount.toFixed(2)} ({calc.rentBasis}) × {calc.rentBasisPeriodsPerYear} = ${calc.annualRent.toFixed(2)}.</p>
                        <p><strong>Gross per payment:</strong> ${calc.annualRent.toFixed(2)} ÷ {calc.paymentPeriodsPerYear} {calc.paymentFrequency} payments = ${calc.grossRentPerPeriod.toFixed(2)}.</p>
                        <p><strong>Agent fee:</strong> ${calc.grossRentPerPeriod.toFixed(2)} × {calc.agentFeePercent.toFixed(2)}% = ${calc.agentFeeAmount.toFixed(2)}.</p>
                        <p><strong>Total deductions:</strong> ${calc.agentFeeAmount.toFixed(2)} + ${calc.otherDeductions.toFixed(2)} = ${calc.totalDeductions.toFixed(2)}.</p>
                        <p><strong>Net to owner:</strong> ${calc.grossRentPerPeriod.toFixed(2)} − ${calc.totalDeductions.toFixed(2)} = ${calc.netOwnerAmount.toFixed(2)}.</p>
                      </div>
                    );
                  })()}
                </CalculationHelp>
                <div className="text-sm space-y-1 mt-3">
                  <div className="flex justify-between">
                    <span className="text-amber-700">Per Period Gross:</span>
                    <span className="font-medium">${previewData.scheduleRows[0]?.grossRentDue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">Agent Fee ({agentFeePercent}%):</span>
                    <span className="font-medium">${previewData.scheduleRows[0]?.agentFeeAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-700">Other Deductions:</span>
                    <span className="font-medium">${otherDeductions.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-amber-200 font-semibold">
                    <span className="text-amber-800">Net to Owner:</span>
                    <span className="font-medium">${previewData.scheduleRows[0]?.netOwnerAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'preview' && previewData && (
        <div className="space-y-4">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="text-sm font-semibold text-slate-800 mb-3">Schedule Summary</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-slate-500">Total Periods</div>
                <div className="text-lg font-semibold">{previewData.periodCount}</div>
              </div>
              <div>
                <div className="text-slate-500">Total Gross Rent</div>
                <div className="text-lg font-semibold text-slate-900">${previewData.totalGross.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-slate-500">Total Deductions</div>
                <div className="text-lg font-semibold text-red-600">${previewData.totalDeductions.toFixed(2)}</div>
              </div>
              <div className="col-span-3 pt-3 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700 font-medium">Total Net to Owner</span>
                  <span className="text-xl font-bold text-emerald-600">${previewData.totalNet.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          <CalculationHelp summary="What the schedule totals mean">
            <p><strong>Total Gross Rent</strong> is the sum of generated gross rent periods. <strong>Total Deductions</strong> is agent fees plus other deductions across those periods. <strong>Total Net to Owner</strong> is Total Gross Rent minus Total Deductions.</p>
            <p className="mt-1">These are expected schedule amounts, not proof of cash received. Actual payment/allocation logic is audited separately.</p>
            {previewData.scheduleRows.some(row => row.metadata?.rentCalculation?.isPartialPeriod) && (
              <p className="mt-1 font-medium text-amber-700">The final period is shorter than a normal payment period. MPP currently keeps the full contractual period amount rather than guessing a pro-rata rule; review that final row before saving.</p>
            )}
          </CalculationHelp>

          <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Due</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Gross</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Deduct</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {previewData.scheduleRows.slice(0, 10).map((row, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-slate-700">{new Date(row.dueDate).toLocaleDateString('en-AU')}</td>
                    <td className="px-3 py-2 text-right text-slate-700">${row.grossRentDue.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-red-600">${row.totalDeductions.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-medium text-emerald-600">${row.netOwnerAmount.toFixed(2)}</td>
                  </tr>
                ))}
                {previewData.scheduleRows.length > 10 && (
                  <tr>
                    <td colSpan="4" className="px-3 py-2 text-center text-xs text-slate-500">
                      ... and {previewData.scheduleRows.length - 10} more rows
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'preview' && !previewData && (
        <div className="bg-slate-50 p-6 text-center rounded-lg border border-slate-200 text-sm text-slate-500">
          Complete the schedule source details and configure settings first to see the preview.
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
        >
          Cancel
        </button>
        {!previewData ? (
          <button
            type="button"
            onClick={() => setActiveTab('selection')}
            className="px-4 py-2 text-sm font-medium text-white bg-slate-400 rounded-lg cursor-not-allowed"
            disabled
          >
            Complete Selection First
          </button>
        ) : (
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : `Generate Schedule (${previewData.periodCount} rows)`}
          </button>
        )}
      </div>
    </form>
  );
};

export default RentScheduleGenerateForm;